/**
 * DataTablePagination Component
 * Complete pagination component with consistent styling
 */

import React from 'react';
import { Box, Pagination, Typography, PaginationProps, Select, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface DataTablePaginationProps extends Omit<PaginationProps, 'count' | 'page' | 'onChange'> {
  totalPages: number;
  page: number;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  showStats?: boolean;
  startIndex?: number;
  endIndex?: number;
  totalResults?: number;
  layout?: 'center' | 'space-between';
  pageSize?: number;
  onPageSizeChange?: (event: any) => void;
  pageSizeOptions?: number[];
  showPageSize?: boolean;
}

export const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  totalPages,
  page,
  onPageChange,
  showStats = false,
  startIndex,
  endIndex,
  totalResults,
  layout = 'center',
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 15, 20, 50],
  showPageSize = false,
  sx,
  ...paginationProps
}) => {
  const theme = useTheme();

  if (totalPages <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.customColors.tableBorder}`,
        ...sx,
      }}
    >
      <Box sx={{ flex: 1 }}>
        {showStats && startIndex !== undefined && endIndex !== undefined && totalResults !== undefined && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Showing {startIndex} to {endIndex} of {totalResults} results
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(3) }}>
        {showPageSize && pageSize !== undefined && onPageSizeChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Rows per page:
            </Typography>
            <Select
              value={pageSize}
              onChange={onPageSizeChange}
              size="small"
              sx={{
                height: 32,
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.customColors.tableBorder,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Pagination
          count={totalPages}
          page={page}
          onChange={onPageChange}
          color="primary"
          shape="rounded"
          sx={{
            '& .MuiPaginationItem-root': {
              color: theme.palette.text.primary,
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
            },
          }}
          {...paginationProps}
        />
      </Box>
    </Box>
  );
};

